import { CaretDownFilled, CaretUpFilled, DownOutlined, HolderOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { dndTransformToCss } from "../components/dndTransform";
import { Checkbox, Spin } from "../ui";
import React from "react";
import "./settingsListRows.css";
import type { ListColumn, ListSortOrder as AntdSortOrder } from "../list/listColumn";
import {
  formatSettingsListScalar,
  getSettingsListNestedRows,
  getSettingsListValueByPath,
  safeSettingsListCellNode,
  settingsListDataIndexPath,
} from "./settingsListDataAccess";
import type { LocalizedText } from "./settingsListTypes";
import { SettingsListColumnGroupContext, useSettingsListColumnGroup } from "./settingsListColumnGroupState";
import { ACTIONS_COLUMN_KEY, ACTIONS_COLUMN_KEY_PREFIX, ALIGN_CENTER, ALIGN_END, ALIGN_RIGHT, SETTINGS_COLUMN_KEY, VALUE_TYPE_OPTION, VALUE_TYPE_TEXT } from "./settingsListConstants";
import { KEY_ARROW_DOWN, KEY_ARROW_UP, KEY_END, KEY_ENTER, KEY_HOME, KEY_SPACE } from "../ui/keyboardKeys";

const TEXT_TRANSFORM_UPPERCASE = "uppercase";
const HEADER_BUTTON_GAP_PX = 8;
const CELL_MEASURE_SAFETY_PX = 2;
const CONTENT_LEVEL_ITERATIONS = 40;
const MIDPOINT_DIVISOR = 2;
const FR_ROUNDING_FACTOR = 100;
const ROW_DRAG_ACTIVATION_DISTANCE_PX = 6;

/**
 * Formatiert Werte von Spalten mit `valueType` (date, money, digit, percent …), die kein eigenes
 * `render` haben. Liefert der Produktadapter keine Funktion, erscheint der Rohwert.
 */
export type SettingsListValueFormatter = (valueType: string, value: unknown) => React.ReactNode | undefined;

type Column<T> = ListColumn<T>;

/** Platzstufe der Liste: Aktionen mit Text, Aktionen nur als Symbol, gestapelte Karten. */
export type SettingsListRowsFit = "full" | "icon" | "stacked";

export interface SettingsListRowsActionProbe<T> {
  render: (record: T, index: number, iconOnly: boolean) => React.ReactNode;
  /** Klassen der Aktionszelle je Darstellung (die Knopfgestaltung hängt daran). */
  className: (iconOnly: boolean) => string;
}

type CellProps = React.HTMLAttributes<HTMLElement> & { colSpan?: number; rowSpan?: number };

export interface SettingsListRowsProps<T extends object> {
  columns: Column<T>[];
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  /** Anzahl/Summen sind für diesen Scope bestätigt; unbekannt und Fehler sind keine Null. */
  summaryKnown?: boolean;
  /** Gestapelte Karten statt Rasterzeilen (schmale Fläche oder Überlauf). */
  stacked: boolean;
  rowKey: (record: T) => string;
  onPageChange: (page: number, pageSize: number) => void;
  // Methodenschreibweise: Aufrufer dürfen den konkreteren Spaltentyp ihrer Liste erwarten.
  onSortChange(column: Column<T>, order: AntdSortOrder): void;
  rowSelection?: {
    selectedRowKeys?: React.Key[];
    onChange?: (selectedRowKeys: React.Key[], selectedRows: T[]) => void;
    getCheckboxProps?: (record: T) => { disabled?: boolean; title?: string };
    hideSelectAll?: boolean;
  };
  /** Einfacher Klick öffnet die Zeile; einen Doppelklick gibt es nicht (Ronny, 18.10.2026). */
  onRowClick?: (record: T) => void;
  onRowClickForSelection?: (record: T, event: React.MouseEvent) => void;
  onActiveRowKeyChange?: (key: React.Key | null, record: T | null) => void;
  rowClassName?: (record: T) => string;
  /**
   * Manuelle Reihenfolge per Ziehgriff (z. B. Reihenfolge eines öffentlichen Katalogs). Der Griff ist ein
   * eigenes Bedienelement vor der ersten Spalte, damit die Zeile antippbar bleibt; Spaltensortierung entfällt.
   * `onReorder` erhält alle Zeilenschlüssel der Seite in neuer Reihenfolge.
   */
  rowReorder?: { onReorder: (orderedKeys: string[]) => void; handleLabel: LocalizedText; disabled?: boolean };
  /** Optional native drag to an external target; manual row ordering keeps its own handle contract. */
  rowDrag?: { canDrag: (record: T) => boolean; onStart: (record: T, event: React.DragEvent<HTMLDivElement>) => void;
    onEnd?: (record: T, event: React.DragEvent<HTMLDivElement>) => void };
  expandedRowRender?: (record: T) => React.ReactNode | null;
  expandedRowKeys?: React.Key[];
  emptyText?: React.ReactNode;
  paginationSummary?: (total: number, range: [number, number]) => React.ReactNode;
  showTotal?: (total: number, range: [number, number]) => React.ReactNode;
  /** Bedienelemente, die in der gestapelten Ansicht über den Karten stehen (z. B. Spalteneditor). */
  stackedHeaderExtra?: React.ReactNode;
  formatValue?: SettingsListValueFormatter;
  /** Aktionen einer Zeile in der gestapelten Ansicht (mit Beschriftung statt nur Symbol). */
  renderStackedActions?: (record: T, index: number) => React.ReactNode;
  /**
   * Meldet die Platzstufe, die in die Fläche passt: Spaltentitel ungekürzt plus Aktionen mit Text
   * (`full`), plus Aktionen nur als Symbol (`icon`), sonst gestapelte Karten (`stacked`). Der Aufrufer
   * schaltet Aktionsdarstellung bzw. Karten; Zelleninhalte werden vorher bis auf die Titelbreite gekürzt.
   */
  onFitChange?: (fit: SettingsListRowsFit) => void;
  /** Aktionen in beiden Darstellungen, unsichtbar gerendert, damit die Platzstufe mit echten Breiten rechnet. */
  actionProbe?: SettingsListRowsActionProbe<T>;
  /** Aufklappbare Zeilen (z. B. Gruppe mit Detailzeilen); Umschalter in der Titelspalte. */
  expandable?: { render: (record: T) => React.ReactNode; rowExpandable?: (record: T) => boolean };
  /**
   * Summenzeile unter den Zeilen: Wert je Spaltenschlüssel (`column.key`), leere Spalten bleiben frei.
   * Die Werte rechnet der Aufrufer (z. B. über alle Daten, nicht nur die sichtbare Seite).
   */
  summaryRow?: Partial<Record<string, React.ReactNode>>;
  /** Ohne Seitenwechsel (eingebettete Detailzeilen). */
  hidePager?: boolean;
  /** Die Listenseite rendert den gemeinsamen Fuß außerhalb ihres Kartenrahmens. */
  hideFooter?: boolean;
  /** Wählbare Seitengrößen; Standard 10/20/50/100. Kurze Vorschaulisten (z. B. in einer Übersicht) beginnen kleiner. */
  pageSizeOptions?: readonly number[];
  /** Ohne Sortierknöpfe im Kopf (eingebettete Detailzeilen). */
  sortable?: boolean;
  /** Karten statt Raster, sobald die eigene Fläche schmaler ist (px); für Listen ohne SettingsListPage. */
  autoStackBelow?: number;
  /** Hervorgehobene Zeile, wenn die Seite sie selbst führt (z. B. Personensuche). */
  activeRowKey?: React.Key | null;
  /**
   * Tastatur: Pfeil hoch/runter, Pos1/Ende bewegen die Hervorhebung (ohne die Seite zu scrollen),
   * Enter öffnet die Zeile, Leertaste wählt sie aus. Greift, solange der Fokus in der Liste oder auf
   * keinem Eingabefeld steht; bei mehreren Listen nur für die erste.
   */
  keyboardNavigation?: boolean;
  text: (value: LocalizedText) => string;
  className?: string;
  style?: React.CSSProperties;
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const SELECTION_TRACK = "40px";
/** Spur des Ziehgriffs: volle Touch-Zielfläche. */
const HANDLE_TRACK = "44px";
/** Klicks auf diese Elemente sind keine Zeilenklicks. */
const INTERACTIVE_TARGET = "button, a, input, textarea, select, label, [role='button'], [role='switch'], [role='checkbox'], .ck-select, .ck-checkbox-wrapper, [aria-haspopup], .ck-popover, .ck-modal, .ck-picker";

function isActionColumn<T>(column: Column<T>): boolean {
  const key = String(column.key ?? "");
  return column.valueType === VALUE_TYPE_OPTION || key === ACTIONS_COLUMN_KEY || key.startsWith(ACTIONS_COLUMN_KEY_PREFIX) || key === SETTINGS_COLUMN_KEY;
}

function titleNode<T>(column: Column<T>, text: (value: LocalizedText) => string): React.ReactNode {
  const title = column.title as unknown;
  if (title == null) return null;
  if (typeof title === "object" && !React.isValidElement(title) && ("de" in (title as object) || "en" in (title as object))) {
    return text(title as LocalizedText);
  }
  if (typeof title === "function") return null;
  return title as React.ReactNode;
}

function titleText<T>(column: Column<T>, text: (value: LocalizedText) => string): string {
  const node = titleNode(column, text);
  return typeof node === "string" || typeof node === "number" ? String(node) : "";
}

/** Zeilenrahmen (je 1 px links und rechts): Im Unterraster liegt er zusätzlich auf den Randspalten. */
const ROW_FRAME_PX = 2;
/** Abstand gegen Hin- und Herschalten an einer Stufengrenze (z. B. durch eine erscheinende Bildlaufleiste). */
const FIT_HYSTERESIS_PX = 20;
const SELECTION_TRACK_PX = 40;
const HANDLE_TRACK_PX = 44;

let headerMeasureCanvas: HTMLCanvasElement | undefined;

/**
 * Breite eines Spaltentitels einzeilig und ungekürzt, gemessen in der echten Kopfschrift
 * (Versalien, Sperrung, Sortierpfeil, eingebettete Knöpfe wie das Zahnrad).
 */
function measureHeaderCell(cell: HTMLElement): number {
  const label = cell.querySelector<HTMLElement>(".slr-sort-label") ?? cell;
  const text = (label.textContent ?? "").trim();
  const style = getComputedStyle(label);
  headerMeasureCanvas ??= document.createElement("canvas");
  const context = headerMeasureCanvas.getContext("2d");
  let width = 0;
  if (text && context) {
    context.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    const shown = style.textTransform === TEXT_TRANSFORM_UPPERCASE ? text.toUpperCase() : text;
    width = context.measureText(shown).width + (Number.parseFloat(style.letterSpacing) || 0) * shown.length;
  } else if (text) {
    width = label.scrollWidth;
  }
  const px = (element: Element, property: "paddingLeft" | "paddingRight" | "columnGap") =>
    Number.parseFloat(getComputedStyle(element)[property]) || 0;
  const sort = cell.querySelector<HTMLElement>(".slr-sort");
  if (sort) {
    // Negative Außenabstände des Sortierknopfs schaffen keinen Platz: seine Breite ist auf die Zelle begrenzt.
    const icon = sort.querySelector<HTMLElement>(".slr-sort-icon");
    width += (icon?.getBoundingClientRect().width ?? 0) + (text ? px(sort, "columnGap") : 0) + px(sort, "paddingLeft") + px(sort, "paddingRight");
  }
  cell.querySelectorAll<HTMLElement>("button:not(.slr-sort)").forEach((button) => {
    width += button.getBoundingClientRect().width + (text ? HEADER_BUTTON_GAP_PX : 0);
  });
  return Math.ceil(width + px(cell, "paddingLeft") + px(cell, "paddingRight") + 1);
}

/** Natürliche Breite einer Zelle: Inhalt (auch wenn er gerade gekürzt ist) plus Baumschalter und Innenabstand. */
function measureCellContent(cell: HTMLElement): number | null {
  const value = cell.querySelector<HTMLElement>(":scope > .slr-cell-value");
  if (!value) return null;
  // Textfelder füllen ihre zugeteilte Spalte. Diese Breite ist kein Inhaltsmaß:
  // erneutes Messen würde das Raster bei jeder Eingabe rückkoppeln.
  // Für solche Zellen gilt die deklarierte Inhalts-/Mindestbreite der Spalte.
  if (value.querySelector('input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]), textarea, select')) return null;
  const style = getComputedStyle(cell);
  // Breite des Inhalts selbst (nicht der zugeteilten Zelle); ohne Range-Geometrie die des Wertelements.
  const range = document.createRange();
  range.selectNodeContents(value);
  const contentWidth = typeof range.getBoundingClientRect === "function" ? range.getBoundingClientRect().width : value.scrollWidth;
  const tree = cell.querySelector<HTMLElement>(":scope > .slr-tree");
  const treeWidth = tree ? tree.getBoundingClientRect().width + (Number.parseFloat(style.columnGap) || 0) : 0;
  return Math.ceil(contentWidth + treeWidth + (Number.parseFloat(style.paddingLeft) || 0) + (Number.parseFloat(style.paddingRight) || 0)) + CELL_MEASURE_SAFETY_PX;
}

/**
 * Kürzt die längsten Inhalte zuerst: Ergebnis ist die Höhe L, bei der jede Spalte
 * min(Inhalt, max(Titel, L)) breit ist und alle zusammen genau die verfügbare Breite füllen.
 */
function contentLevel(mins: number[], maxs: number[], target: number): number {
  let low = 0;
  let high = Math.max(0, ...maxs);
  for (let step = 0; step < CONTENT_LEVEL_ITERATIONS; step += 1) {
    const mid = (low + high) / MIDPOINT_DIVISOR;
    const sum = mins.reduce((total, min, index) => total + Math.min(maxs[index], Math.max(min, mid)), 0);
    if (sum > target) high = mid;
    else low = mid;
  }
  return low;
}

const fr = (value: number) => `${Math.max(1, Math.round(value * FR_ROUNDING_FACTOR) / FR_ROUNDING_FACTOR)}fr`;

function nextOrder(order: AntdSortOrder): AntdSortOrder {
  if (order === "ascend") return "descend";
  if (order === "descend") return null;
  return "ascend";
}

function cellContent<T extends object>(column: Column<T>, record: T, index: number, formatValue?: SettingsListValueFormatter): React.ReactNode {
  const path = settingsListDataIndexPath(column.dataIndex);
  let value = getSettingsListValueByPath(record, path);
  const renderText = (column as { renderText?: (value: unknown, record: T, index: number) => unknown }).renderText;
  if (typeof renderText === "function") value = renderText(value, record, index);
  const valueEnum = column.valueEnum as Record<string, { text?: React.ReactNode } | React.ReactNode> | undefined;
  if (valueEnum && value != null && !column.render) {
    const entry = valueEnum[String(value)];
    if (entry != null) return typeof entry === "object" && !React.isValidElement(entry) && "text" in (entry as object) ? (entry as { text?: React.ReactNode }).text : (entry as React.ReactNode);
  }
  if (typeof column.render === "function") {
    return safeSettingsListCellNode((column.render as (value: unknown, record: T, index: number, action?: unknown) => unknown)(value, record, index, undefined));
  }
  const valueType = typeof column.valueType === "string" ? column.valueType : undefined;
  if (valueType && valueType !== VALUE_TYPE_TEXT && value != null && value !== "") {
    const formatted = formatValue?.(valueType, value);
    if (formatted !== undefined) return formatted;
  }
  return formatSettingsListScalar(value) ?? (path ? "" : null);
}

function Pager({ page, pageSize, pageSizeOptions, total, onChange, text }: { page: number; pageSize: number; pageSizeOptions: readonly number[]; total: number; onChange: (page: number, size: number) => void; text: (value: LocalizedText) => string }) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const pages: Array<number | "gap"> = [];
  const push = (value: number | "gap") => { if (pages[pages.length - 1] !== value) pages.push(value); };
  for (let p = 1; p <= pageCount; p += 1) {
    if (p === 1 || p === pageCount || Math.abs(p - page) <= 1) push(p);
    else push("gap");
  }
  const sizes = pageSizeOptions.includes(pageSize) ? pageSizeOptions : [...pageSizeOptions, pageSize].sort((a, b) => a - b);
  return (
    <nav className="slr-pager" aria-label={text("Pagination")}>
      <button type="button" className="slr-pager-step" disabled={page <= 1} aria-label={text("Previous")} title={text("Previous")} onClick={() => onChange(page - 1, pageSize)}>
        <LeftOutlined />
      </button>
      {pages.map((entry, index) =>
        entry === "gap" ? (
          <span key={`gap-${index}`} className="slr-pager-gap" aria-hidden="true">…</span>
        ) : (
          <button key={entry} type="button" className={entry === page ? "slr-pager-page is-current" : "slr-pager-page"} aria-current={entry === page ? "page" : undefined} onClick={() => onChange(entry, pageSize)}>
            {entry}
          </button>
        )
      )}
      <button type="button" className="slr-pager-step" disabled={page >= pageCount} aria-label={text("Next")} title={text("Next")} onClick={() => onChange(page + 1, pageSize)}>
        <RightOutlined />
      </button>
      <label className="slr-pager-size">
        <select value={pageSize} aria-label={text("Rows per page")} onChange={(event) => onChange(1, Number(event.target.value))}>
          {sizes.map((size) => <option key={size} value={size}>{size} / {text("page")}</option>)}
        </select>
      </label>
    </nav>
  );
}

/**
 * Darstellung der Einstellungsliste ohne Antd-Tabelle: Rasterzeilen mit eigener Kopfzeile, die bei
 * Platzmangel zu gestapelten Karten werden. Daten, Sortierung, Seitengröße und Spalten liefert
 * `SettingsListPage`; diese Komponente rendert nur und meldet Seiten- und Sortierwechsel zurück.
 * Optik über die Variablen `--slr-*` (`settingsListRows.css`), die der Produktstil setzt.
 */
export function SettingsListRows<T extends object>({
  columns,
  rows,
  total,
  page,
  pageSize,
  loading,
  summaryKnown = !loading,
  stacked,
  rowKey,
  onPageChange,
  onSortChange,
  rowSelection,
  onRowClick,
  onRowClickForSelection,
  onActiveRowKeyChange,
  rowClassName,
  rowReorder,
  rowDrag,
  expandedRowRender,
  expandedRowKeys,
  emptyText,
  paginationSummary,
  showTotal,
  stackedHeaderExtra,
  formatValue,
  renderStackedActions,
  onFitChange,
  actionProbe,
  expandable,
  summaryRow,
  hidePager = false,
  hideFooter = false,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  sortable: sortingEnabled = true,
  autoStackBelow,
  activeRowKey,
  keyboardNavigation = false,
  text,
  className,
  style,
}: SettingsListRowsProps<T>) {
  const groupedColumns = React.useContext(SettingsListColumnGroupContext) !== null;
  const [openTreeKeys, setOpenTreeKeys] = React.useState<ReadonlySet<string>>(() => new Set());
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [narrowSelf, setNarrowSelf] = React.useState(false);
  React.useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !autoStackBelow || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => setNarrowSelf(entry.contentRect.width < autoStackBelow));
    observer.observe(root);
    return () => observer.disconnect();
  }, [autoStackBelow]);
  stacked = stacked || narrowSelf;
  const gridRef = React.useRef<HTMLDivElement>(null);
  // Platzverteilung (Ronny, 16.10.2026): Spaltentitel stehen einzeilig und ungekürzt, ihre Breite ist die
  // Untergrenze jeder Spalte. Fehlt Platz, werden zuerst die längsten Zelleninhalte gekürzt (bis auf die
  // Titelbreite), dann zeigen die Aktionen nur Symbole, dann gibt es Karten. Titel werden nur gekürzt,
  // wenn der Aufrufer keine Karten zulässt und selbst die Titel nicht passen.
  const [availableWidth, setAvailableWidth] = React.useState(0);
  React.useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    setAvailableWidth(root.clientWidth);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => setAvailableWidth(root.clientWidth));
    observer.observe(root);
    return () => observer.disconnect();
  }, []);
  // Webfonts ändern die Textbreiten: nach dem Laden neu messen.
  const [fontsVersion, setFontsVersion] = React.useState(0);
  React.useEffect(() => {
    let active = true;
    void document.fonts?.ready.then(() => { if (active) setFontsVersion((version) => version + 1); });
    return () => { active = false; };
  }, []);

  // --- Tastatur: hervorgehobene Zeile ---
  const [keyboardActiveKey, setKeyboardActiveKey] = React.useState<string | null>(null);
  const controlledActive = activeRowKey !== undefined;
  const effectiveActiveKey = controlledActive ? (activeRowKey == null ? null : String(activeRowKey)) : keyboardActiveKey;
  const keyboardStateRef = React.useRef({ rows, rowKey, effectiveActiveKey, onActiveRowKeyChange, onRowClick, rowSelection });
  keyboardStateRef.current = { rows, rowKey, effectiveActiveKey, onActiveRowKeyChange, onRowClick, rowSelection };
  const toggleSelectionRef = React.useRef<(record: T) => void>(() => undefined);
  React.useEffect(() => {
    if (!keyboardNavigation) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const root = rootRef.current;
      if (!root || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if (document.querySelector(".slr[data-keyboard-nav]") !== root) return;
      const target = event.target as HTMLElement | null;
      const insideList = Boolean(target && root.contains(target));
      const onPage = !target || target === document.body || target === document.documentElement;
      if (!insideList && !onPage) return;
      if (insideList && target?.closest("input, textarea, select, [contenteditable='true'], .ck-select, .ck-picker")) return;
      if (document.querySelector(".ck-modal-root:not([style*='display: none']), .ck-sheet:not([style*='display: none'])")) return;
      const state = keyboardStateRef.current;
      if (state.rows.length === 0) return;
      const keys = state.rows.map(state.rowKey);
      const currentIndex = state.effectiveActiveKey == null ? -1 : keys.indexOf(state.effectiveActiveKey);
      let nextIndex: number | null = null;
      if (event.key === KEY_ARROW_DOWN) nextIndex = Math.min(keys.length - 1, currentIndex + 1);
      else if (event.key === KEY_ARROW_UP) nextIndex = currentIndex < 0 ? 0 : Math.max(0, currentIndex - 1);
      else if (event.key === KEY_HOME) nextIndex = 0;
      else if (event.key === KEY_END) nextIndex = keys.length - 1;
      if (nextIndex != null) {
        event.preventDefault();
        const record = state.rows[nextIndex];
        setKeyboardActiveKey(keys[nextIndex]);
        state.onActiveRowKeyChange?.(keys[nextIndex], record);
        const rowElement = root.querySelector<HTMLElement>(`.slr-row[data-row-key="${CSS.escape(keys[nextIndex])}"]`);
        rowElement?.scrollIntoView({ block: "nearest" });
        return;
      }
      if (currentIndex < 0 || (insideList && target?.closest("button, a, [role='button'], [role='checkbox']"))) return;
      const record = state.rows[currentIndex];
      if (event.key === KEY_ENTER) {
        const open = state.onRowClick;
        if (!open) return;
        event.preventDefault();
        open(record);
      } else if (event.key === KEY_SPACE && state.rowSelection) {
        event.preventDefault();
        toggleSelectionRef.current(record);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [keyboardNavigation]);
  const dataColumns = columns.filter((column) => !isActionColumn(column));
  const primaryColumn = dataColumns.find((column) => column.listRole === "primary") ?? dataColumns.find((column) => column.listRole !== "media");
  const hasSelection = Boolean(rowSelection);
  const hasReorder = Boolean(rowReorder) && rows.length > 1;
  // Titelbreiten aus der echten Kopfzeile (einzeilig, ungekürzt).
  const [headMeasured, setHeadMeasured] = React.useState<Record<string, number>>({});
  React.useLayoutEffect(() => {
    const head = gridRef.current?.querySelector<HTMLElement>(".slr-head");
    if (!head) return;
    const next: Record<string, number> = {};
    head.querySelectorAll<HTMLElement>(":scope > .slr-head-cell[data-col]").forEach((cell) => {
      next[cell.dataset.col as string] = measureHeaderCell(cell);
    });
    setHeadMeasured((current) => (JSON.stringify(current) === JSON.stringify(next) ? current : next));
  }, [columns, text, stacked, fontsVersion]);
  // Inhaltsbreiten der sichtbaren Zeilen aus dem DOM (volle Breite trotz Auslassungspunkten).
  const [contentMeasured, setContentMeasured] = React.useState<Record<string, number>>({});
  React.useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid || stacked) return;
    const next: Record<string, number> = {};
    grid.querySelectorAll<HTMLElement>(".slr-row:nth-child(-n+30) > .slr-cell[data-col]").forEach((cell) => {
      if (cell.style.gridColumn) return;
      const width = measureCellContent(cell);
      if (width == null) return;
      const key = cell.dataset.col as string;
      next[key] = Math.max(next[key] ?? 0, width);
    });
    setContentMeasured((current) => (JSON.stringify(current) === JSON.stringify(next) ? current : next));
  }, [rows, columns, stacked, fontsVersion]);
  // Aktionsbreiten: gerendert (Spaltenbreite im Raster) und, falls der Aufrufer sie liefert, beide
  // Darstellungen aus der unsichtbaren Messfläche. Ohne Messfläche gilt die gerenderte Breite für beide.
  // Die Messfläche steht nur für einen Messdurchlauf im Dokument (vor dem Zeichnen wieder entfernt), damit
  // ihre Kopien der Aktionen weder als Bedienelemente noch über ihre Beschriftung auffindbar sind. Neu
  // gemessen wird, sobald sich Zeilen, Aktionen, Schriften oder die gerenderte Aktionsbreite (z. B. Zoom) ändern.
  const [actionMeasured, setActionMeasured] = React.useState({ rendered: 0, full: 0, icon: 0 });
  const [naturalActionWidths, setNaturalActionWidths] = React.useState<Record<string, number>>({});
  const probeInputs = React.useMemo(() => ({}), [rows, actionProbe, fontsVersion, actionMeasured.rendered]);
  const [probedInputs, setProbedInputs] = React.useState<object>();
  const probing = Boolean(actionProbe) && rows.length > 0 && probedInputs !== probeInputs;
  React.useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const widest = (selector: string) =>
      Array.from(root.querySelectorAll<HTMLElement>(selector)).reduce((max, element) => Math.max(max, Math.ceil(element.getBoundingClientRect().width)), 0);
    const rendered = stacked
      ? actionMeasured.rendered
      : Array.from(root.querySelectorAll<HTMLElement>(":scope > .slr-grid > .slr-head > .slr-cell--actions")).reduce((sum, cell) => sum + Math.ceil(cell.getBoundingClientRect().width), 0);
    setActionMeasured((current) => {
      const full = !actionProbe ? rendered : probing ? widest(":scope > .slr-probe > [data-probe='full']") : current.full;
      const icon = !actionProbe ? rendered : probing ? widest(":scope > .slr-probe > [data-probe='icon']") : current.icon;
      return current.rendered === rendered && current.full === full && current.icon === icon ? current : { rendered, full, icon };
    });
    if (probing) setProbedInputs(probeInputs);
  });

  React.useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid || stacked || !groupedColumns) return;
    const next: Record<string, number> = {};
    grid.querySelectorAll<HTMLElement>(".slr-cell--actions[data-layout-col]").forEach(cell => {
      const key = cell.dataset.layoutCol as string;
      const width = cell.classList.contains("slr-head-cell") ? measureHeaderCell(cell) : measureCellContent(cell);
      next[key] = Math.max(next[key] ?? 0, width ?? 0);
    });
    setNaturalActionWidths(current => JSON.stringify(current) === JSON.stringify(next) ? current : next);
  }, [columns, rows, stacked, fontsVersion, groupedColumns]);

  const columnKeyOf = (column: Column<T>, index: number) => String(column.key ?? index);
  const declaredMinOf = (column: Column<T>) => {
    const declared = typeof column.minWidth === "number" ? column.minWidth : typeof column.width === "number" ? column.width : 0;
    return Math.max(0, declared);
  };
  const localDataLayout = columns.flatMap((column, index) => {
    if (isActionColumn(column)) return [];
    const key = columnKeyOf(column, index);
    const title = Math.max(headMeasured[key] ?? 0, declaredMinOf(column));
    const content = Math.max(title, contentMeasured[key] ?? column.contentWidth ?? title);
    return [{ key, title, content }];
  });
  const groupLayout = useSettingsListColumnGroup({
    schema: JSON.stringify([hasSelection, hasReorder, columns.map((column, index) => [columnKeyOf(column, index), isActionColumn(column)])]),
    columns: columns.map((column, index) => {
      const key = columnKeyOf(column, index);
      const action = isActionColumn(column);
      const data = localDataLayout.find(item => item.key === key);
      return { key, title: data?.title ?? 0, content: action ? naturalActionWidths[key] ?? 0 : data?.content ?? 0, action };
    }),
    fullActions: actionMeasured.full,
    iconActions: actionMeasured.icon,
    availableWidth,
  });
  const dataLayout = groupLayout ? groupLayout.columns.filter(column => !column.action) : localDataLayout;
  const groupedActionWidth = groupLayout?.columns.filter(column => column.action).reduce((sum, column) => sum + column.content, 0);
  const layoutWidth = groupLayout?.availableWidth ?? availableWidth;
  const fullActionWidth = groupLayout?.fullActions ?? actionMeasured.full;
  const iconActionWidth = groupLayout?.iconActions ?? actionMeasured.icon;
  const measured = Object.keys(headMeasured).length > 0 && availableWidth > 0;
  const titleSum = dataLayout.reduce((sum, column) => sum + column.title, 0);
  const contentSum = dataLayout.reduce((sum, column) => sum + column.content, 0);
  const frameWidth = ROW_FRAME_PX + (hasSelection ? SELECTION_TRACK_PX : 0) + (hasReorder ? HANDLE_TRACK_PX : 0);
  const dataWidth = layoutWidth - frameWidth - (groupedActionWidth ?? actionMeasured.rendered);
  // Titel passen nicht einmal ohne Inhaltsluft (nur ohne Karten möglich): Titel kürzen statt seitlich überlaufen.
  const squeezed = !stacked && measured && titleSum > dataWidth;
  const level = measured && !squeezed && contentSum > dataWidth
    ? contentLevel(dataLayout.map((column) => column.title), dataLayout.map((column) => column.content), dataWidth)
    : null;
  const dataTracks = new Map(dataLayout.map((column) => {
    if (!measured) return [column.key, `minmax(${Math.round(column.title)}px, ${fr(column.content)})`];
    if (squeezed) return [column.key, `minmax(0, ${fr(column.title)})`];
    if (level == null) return [column.key, `minmax(${Math.round(column.content)}px, ${fr(column.content)})`];
    const target = Math.min(column.content, Math.max(column.title, level));
    return [column.key, `minmax(${Math.round(column.title)}px, ${fr(target)})`];
  }));
  const template = [hasReorder ? HANDLE_TRACK : null, hasSelection ? SELECTION_TRACK : null, ...columns.map((column, index) =>
    isActionColumn(column)
      ? groupLayout ? `${groupLayout.columns[index].content}px` : "max-content"
      : dataTracks.get(columnKeyOf(column, index)) ?? "minmax(0, 1fr)"
  )].filter(Boolean).join(" ");

  // Platzstufe melden: Titel ungekürzt plus Aktionen mit Text bzw. nur Symbol, sonst Karten.
  const fitRef = React.useRef<SettingsListRowsFit>("full");
  const onFitChangeRef = React.useRef(onFitChange);
  onFitChangeRef.current = onFitChange;
  React.useLayoutEffect(() => {
    if (!measured || (fullActionWidth <= 0 && columns.some(isActionColumn))) return;
    const previous = fitRef.current;
    const fits = (actions: number, raising: boolean) => titleSum + frameWidth + actions + (raising ? FIT_HYSTERESIS_PX : 0) <= layoutWidth;
    const next: SettingsListRowsFit = fits(fullActionWidth, previous !== "full")
      ? "full"
      : fits(iconActionWidth, previous === "stacked") ? "icon" : "stacked";
    if (next === previous) return;
    fitRef.current = next;
    onFitChangeRef.current?.(next);
  }, [measured, titleSum, frameWidth, layoutWidth, fullActionWidth, iconActionWidth, columns]);
  const selectedKeys = rowSelection?.selectedRowKeys ?? [];
  const isSelected = (key: string) => selectedKeys.some((selected) => String(selected) === key);
  const selectableRows = rows.filter((record) => !rowSelection?.getCheckboxProps?.(record)?.disabled);
  const allSelected = selectableRows.length > 0 && selectableRows.every((record) => isSelected(rowKey(record)));
  const someSelected = !allSelected && selectableRows.some((record) => isSelected(rowKey(record)));
  const expandedSet = new Set((expandedRowKeys ?? []).map(String));

  const setSelection = (keys: React.Key[]) => {
    const keySet = new Set(keys.map(String));
    rowSelection?.onChange?.(keys, rows.filter((record) => keySet.has(rowKey(record))));
  };
  const toggleRowSelection = (record: T, event: React.MouseEvent) => {
    const key = rowKey(record);
    if (rowSelection?.getCheckboxProps?.(record)?.disabled) return;
    if (rowSelection?.onChange) {
      setSelection(isSelected(key) ? selectedKeys.filter((selected) => String(selected) !== key) : [...selectedKeys, key]);
      return;
    }
    onRowClickForSelection?.(record, event);
  };

  toggleSelectionRef.current = (record: T) => toggleRowSelection(record, {} as React.MouseEvent);

  const renderSelectionCell = (record: T) => {
    if (!rowSelection) return null;
    const key = rowKey(record);
    const checkbox = rowSelection.getCheckboxProps?.(record);
    return (
      <div className="slr-cell slr-cell--select" onClick={(event) => event.stopPropagation()}>
        <Checkbox checked={isSelected(key)} disabled={checkbox?.disabled} title={checkbox?.title} aria-label={text("Select")} onChange={(event) => toggleRowSelection(record, event.nativeEvent as unknown as React.MouseEvent)} />
      </div>
    );
  };

  const renderRow = (record: T, index: number, depth: number): React.ReactNode => {
    const key = rowKey(record);
    const children = getSettingsListNestedRows(record, "children");
    const canExpand = Boolean(expandable && (expandable.rowExpandable?.(record) ?? true));
    const treeOpen = openTreeKeys.has(key);
    const selected = isSelected(key);
    const clickable = Boolean(onRowClick || onRowClickForSelection || onActiveRowKeyChange);
    const expanded = expandedSet.has(key) ? expandedRowRender?.(record) : canExpand && treeOpen ? expandable?.render(record) : null;
    const handleClick = (event: React.MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest(INTERACTIVE_TARGET)) return;
      onActiveRowKeyChange?.(key, record);
      if (rowSelection && onRowClickForSelection) {
        onRowClickForSelection(record, event);
        return;
      }
      onRowClick?.(record);
    };
    const treeToggle = children.length > 0 || canExpand ? (
      <button
        type="button"
        className={treeOpen ? "slr-tree-toggle is-open" : "slr-tree-toggle"}
        aria-expanded={treeOpen}
        aria-label={text(treeOpen ? "Collapse" : "Expand")}
        onClick={(event) => {
          event.stopPropagation();
          setOpenTreeKeys((current) => {
            const next = new Set(current);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
          });
        }}
      >
        <DownOutlined />
      </button>
    ) : null;

    let skip = 0;
    const cells = columns.map((column, columnIndex) => {
      const cellProps = ((column.onCell as ((record: T, index?: number) => CellProps) | undefined)?.(record, index) ?? {}) as CellProps;
      const { colSpan, rowSpan: _rowSpan, className: cellClassName, style: cellStyle, onClick: cellOnClick, ...restCellProps } = cellProps;
      if (skip > 0) {
        skip -= 1;
        return null;
      }
      if (colSpan === 0) return null;
      if (colSpan != null && colSpan > 1) skip = colSpan - 1;
      const action = isActionColumn(column);
      const content = action && stacked && renderStackedActions ? renderStackedActions(record, index) : cellContent(column, record, index, formatValue);
      const label = titleText(column, text);
      const isFirstData = !action && column === primaryColumn;
      const isMedia = !action && column.listRole === "media";
      const ellipsis = !action && column.ellipsis !== false;
      return (
        <div
          key={String(column.key ?? columnIndex)}
          {...(restCellProps as React.HTMLAttributes<HTMLDivElement>)}
          data-col={action ? undefined : String(column.key ?? columnIndex)}
          data-layout-col={String(column.key ?? columnIndex)}
          className={["slr-cell", action ? "slr-cell--actions" : "", isFirstData ? "slr-cell--primary" : "", isMedia ? "slr-cell--media" : "", column.align === ALIGN_RIGHT || column.align === ALIGN_END ? "slr-cell--end" : column.align === ALIGN_CENTER ? "slr-cell--center" : "", ellipsis ? "slr-cell--ellipsis" : "", cellClassName ?? "", typeof column.className === "string" ? column.className : ""].filter(Boolean).join(" ")}
          style={{
            ...(cellStyle as React.CSSProperties),
            minWidth: 0,
            maxWidth: undefined,
            width: undefined,
            ...(colSpan != null && colSpan > 1 ? { gridColumn: `span ${colSpan}` } : {}),
          }}
          data-label={stacked && !action && !isFirstData && !isMedia && label ? label : undefined}
          onClick={cellOnClick as React.MouseEventHandler<HTMLDivElement> | undefined}
        >
          {isFirstData && (depth > 0 || treeToggle) ? (
            <span className="slr-tree" style={{ "--slr-depth": depth } as React.CSSProperties}>
              {treeToggle ?? <span className="slr-tree-spacer" aria-hidden="true" />}
            </span>
          ) : null}
          <div className="slr-cell-value">{content}</div>
        </div>
      );
    });

    return (
      <React.Fragment key={key}>
        <SortableRowShell
          id={key}
          enabled={hasReorder && depth === 0}
          handleLabel={rowReorder ? text(rowReorder.handleLabel) : ""}
          handleDisabled={Boolean(rowReorder?.disabled)}
          role="row"
          className={[
            "slr-row",
            depth > 0 ? "slr-row--child" : "",
            clickable ? "is-clickable" : "",
            selected ? "is-selected" : "",
            !controlledActive && effectiveActiveKey === key ? "is-active" : "",
            expanded ? "is-expanded" : "",
            rowClassName?.(record) ?? "",
          ].filter(Boolean).join(" ")}
          data-row-key={key}
          draggable={rowDrag && !rowReorder ? rowDrag.canDrag(record) : undefined}
          onDragStart={rowDrag && !rowReorder ? event => {
            if (!rowDrag.canDrag(record) || (event.target as HTMLElement).closest(INTERACTIVE_TARGET)) { event.preventDefault(); return; }
            rowDrag.onStart(record, event);
          } : undefined}
          onDragEnd={rowDrag && !rowReorder ? event => rowDrag.onEnd?.(record, event) : undefined}
          onClick={clickable ? handleClick : undefined}
        >
          {renderSelectionCell(record)}
          {cells}
        </SortableRowShell>
        {expanded ? <div className="slr-expanded">{expanded}</div> : null}
        {treeOpen && !canExpand ? children.map((child, childIndex) => renderRow(child, childIndex, depth + 1)) : null}
      </React.Fragment>
    );
  };

  return (
    <div
      ref={rootRef}
      className={["slr", stacked ? "slr--stacked" : "slr--grid", squeezed ? "slr--squeezed" : "", loading ? "is-loading" : "", className ?? ""].filter(Boolean).join(" ")}
      style={style}
      role="table"
      aria-busy={loading || undefined}
      aria-rowcount={total}
      data-keyboard-nav={keyboardNavigation ? "" : undefined}
    >
      {/* Ein gemeinsames Raster für Kopf und alle Zeilen (Unterraster), damit Spalten bündig bleiben. */}
      <div ref={gridRef} className="slr-grid" inert={loading || undefined} style={stacked ? undefined : { gridTemplateColumns: template }}>
      {stacked ? (
        stackedHeaderExtra || (hasSelection && !rowSelection?.hideSelectAll) ? (
          <div className="slr-stacked-head">
            {hasSelection && !rowSelection?.hideSelectAll ? (
              <Checkbox checked={allSelected} indeterminate={someSelected} disabled={selectableRows.length === 0} onChange={() => setSelection(allSelected ? [] : selectableRows.map(rowKey))}>
                {text("Select all")}
              </Checkbox>
            ) : <span />}
            {stackedHeaderExtra}
          </div>
        ) : null
      ) : (
        <div role="row" className="slr-head">
          {hasReorder ? <div className="slr-head-cell slr-cell--handle" aria-hidden="true" /> : null}
          {hasSelection ? (
            <div className="slr-head-cell slr-cell--select">
              {rowSelection?.hideSelectAll ? null : (
                <Checkbox checked={allSelected} indeterminate={someSelected} disabled={selectableRows.length === 0} aria-label={text("Select all")} onChange={() => setSelection(allSelected ? [] : selectableRows.map(rowKey))} />
              )}
            </div>
          ) : null}
          {columns.map((column, columnIndex) => {
            const node = titleNode(column, text);
            const sortable = sortingEnabled && !hasReorder && column.sorter != null && column.sorter !== false && !isActionColumn(column);
            const order = (column.sortOrder ?? null) as AntdSortOrder;
            const classNames = ["slr-head-cell", isActionColumn(column) ? "slr-cell--actions" : "", column.align === ALIGN_RIGHT || column.align === ALIGN_END ? "slr-cell--end" : column.align === ALIGN_CENTER ? "slr-cell--center" : ""].filter(Boolean).join(" ");
            const dataCol = isActionColumn(column) ? undefined : columnKeyOf(column, columnIndex);
            if (!sortable) {
              return <div key={String(column.key ?? columnIndex)} role="columnheader" className={classNames} data-col={dataCol} data-layout-col={columnKeyOf(column, columnIndex)} title={titleText(column, text) || undefined}>{node}</div>;
            }
            return (
              <div key={String(column.key ?? columnIndex)} role="columnheader" className={classNames} data-col={dataCol} title={titleText(column, text) || undefined} aria-sort={order === "ascend" ? "ascending" : order === "descend" ? "descending" : "none"}>
                <button type="button" className={order ? "slr-sort is-active" : "slr-sort"} onClick={() => onSortChange(column, nextOrder(order))}>
                  <span className="slr-sort-label">{node}</span>
                  <span className="slr-sort-icon" aria-hidden="true">
                    <CaretUpFilled className={order === "ascend" ? "is-on" : undefined} />
                    <CaretDownFilled className={order === "descend" ? "is-on" : undefined} />
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="slr-body" role="rowgroup">
        {rows.length === 0 ? (
          <div className={loading ? "slr-loading-placeholder" : "slr-empty"}>{loading ? null : emptyText ?? text("No data")}</div>
        ) : hasReorder && rowReorder ? (
          <RowReorderContext keys={rows.map(rowKey)} onReorder={rowReorder.onReorder}>
            {rows.map((record, index) => renderRow(record, index, 0))}
          </RowReorderContext>
        ) : (
          rows.map((record, index) => renderRow(record, index, 0))
        )}
        {summaryRow && rows.length > 0 ? (
          <div role="row" className="slr-row slr-row--summary">
            {hasReorder && !stacked ? <div className="slr-cell slr-cell--handle" /> : null}
            {hasSelection && !stacked ? <div className="slr-cell slr-cell--select" /> : null}
            {columns.map((column, columnIndex) => {
              const key = String(column.key ?? columnIndex);
              const value = summaryRow[key];
              if (stacked && value == null) return null;
              const label = titleText(column, text);
              const align = column.align === ALIGN_RIGHT || column.align === ALIGN_END ? "slr-cell--end" : column.align === ALIGN_CENTER ? "slr-cell--center" : "";
              return (
                <div key={key} className={["slr-cell", align].filter(Boolean).join(" ")} data-label={stacked && label ? label : undefined}>
                  <div className="slr-cell-value">{value ?? null}</div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
      </div>

      {loading ? <div className="slr-loading-indicator"><Spin /></div> : null}
      {probing && actionProbe ? (
        // Messfläche: Aktionen jeder Zeile mit Text und nur als Symbol, unsichtbar, ohne Bedienung, nur für einen Messdurchlauf.
        <div className="slr-probe" aria-hidden="true" inert>
          {rows.map((record, index) => (
            <React.Fragment key={rowKey(record)}>
              <div className={`slr-cell slr-cell--actions ${actionProbe.className(false)}`} data-probe="full">
                <div className="slr-cell-value">{actionProbe.render(record, index, false)}</div>
              </div>
              <div className={`slr-cell slr-cell--actions ${actionProbe.className(true)}`} data-probe="icon">
                <div className="slr-cell-value">{actionProbe.render(record, index, true)}</div>
              </div>
            </React.Fragment>
          ))}
        </div>
      ) : null}

      {!hideFooter && <SettingsListFooter total={total} page={page} pageSize={pageSize} pageSizeOptions={pageSizeOptions}
        hidePager={hidePager} summaryKnown={summaryKnown} paginationSummary={paginationSummary} showTotal={showTotal}
        onPageChange={onPageChange} text={text} />}
    </div>
  );
}

/** Gemeinsame Status- und Paginationszeile, unabhängig vom Seitenblättern. */
export function SettingsListFooter({ total, page, pageSize, pageSizeOptions = PAGE_SIZE_OPTIONS, hidePager = false,
  summaryKnown = true, paginationSummary, showTotal, onPageChange, text, style,
}: Pick<SettingsListRowsProps<object>, "total" | "page" | "pageSize" | "pageSizeOptions" | "hidePager" |
  "summaryKnown" | "paginationSummary" | "showTotal" | "onPageChange" | "text" | "style">) {
  const range: [number, number] = [total === 0 ? 0 : (page - 1) * pageSize + 1, Math.min(total, page * pageSize)];
  // Vor dem ersten Ergebnis ist eine aus [] berechnete Summe nicht bestätigt.
  const summary = !summaryKnown ? null : paginationSummary?.(total, range) ?? showTotal?.(total, range) ?? null;
  const showPager = !hidePager && total > Math.min(pageSize, pageSizeOptions[0] ?? pageSize);
  if (summary == null && !showPager) return null;
  return <div className="slr-foot" style={style}>
    {summary != null && <div className="slr-foot-summary">{summary}</div>}
    {showPager && <Pager page={page} pageSize={pageSize} pageSizeOptions={pageSizeOptions} total={total} onChange={onPageChange} text={text} />}
  </div>;
}

/** Ziehen-und-Ablegen der Zeilen einer Seite; 6 px Auslöseabstand, damit Taps auf dem Smartphone Taps bleiben. */
function RowReorderContext({ keys, onReorder, children }: { keys: string[]; onReorder: (orderedKeys: string[]) => void; children: React.ReactNode }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: ROW_DRAG_ACTIVATION_DISTANCE_PX } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const onDragEnd = (event: DragEndEvent) => {
    if (!event.over || event.active.id === event.over.id) return;
    const from = keys.indexOf(String(event.active.id));
    const to = keys.indexOf(String(event.over.id));
    if (from < 0 || to < 0) return;
    const next = [...keys];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next);
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={keys} strategy={verticalListSortingStrategy}>{children}</SortableContext>
    </DndContext>
  );
}

type SortableRowShellProps = React.HTMLAttributes<HTMLDivElement> & {
  id: string;
  enabled: boolean;
  handleLabel: string;
  handleDisabled: boolean;
  "data-row-key"?: string;
};

/** Zeilenelement; mit Zeilensortierung trägt es den Ziehgriff als erste Zelle und folgt der Ziehbewegung. */
function SortableRowShell({ id, enabled, handleLabel, handleDisabled, children, style, ...rest }: SortableRowShellProps) {
  if (!enabled) return <div {...rest} style={style}>{children}</div>;
  return <SortableRowShellInner id={id} handleLabel={handleLabel} handleDisabled={handleDisabled} style={style} {...rest}>{children}</SortableRowShellInner>;
}

function SortableRowShellInner({ id, handleLabel, handleDisabled, children, style, className, ...rest }: Omit<SortableRowShellProps, "enabled">) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: handleDisabled });
  return (
    <div
      {...rest}
      ref={setNodeRef}
      className={[className, isDragging ? "is-dragging" : ""].filter(Boolean).join(" ")}
      style={{ ...style, transform: dndTransformToCss(transform), transition }}
    >
      <div className="slr-cell slr-cell--handle">
        <button
          type="button"
          className="slr-row-handle"
          aria-label={handleLabel}
          title={handleLabel}
          disabled={handleDisabled}
          {...attributes}
          {...listeners}
        >
          <HolderOutlined />
        </button>
      </div>
      {children}
    </div>
  );
}
